import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAsyncAction } from '../hooks/useAsyncAction';
import DashboardLayout from '../components/DashboardLayout';

const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const fmtAmount = (cents, currency) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

function TptForeverNote({ novels, showTitles }) {
  const plural = novels.length > 1;
  const titles = showTitles ? ` (${novels.map((n) => n.title).join(', ')})` : '';
  return (
    <p style={{ ...styles.cardText, marginTop: '8px', fontSize: '13px', color: '#9ca3af' }}>
      Your TpT novel{plural ? 's' : ''}{titles} remain{plural ? '' : 's'} free forever regardless of subscription status.
    </p>
  );
}

export default function AccountPage() {
  const { teacher, logout, updateTeacher, fetchMe, isLightweightAccount, isSubscriber } = useAuth();
  const navigate = useNavigate();

  const [classroomName, setClassroomName] = useState(teacher?.classroom_name || '');
  const [classroomNameSaved, setClassroomNameSaved] = useState(false);
  const classroomNameAction = useAsyncAction();

  const [invoices, setInvoices] = useState([]);
  const { loading: invoicesLoading, run: runInvoices } = useAsyncAction();

  // Cancel flow
  const [cancelConfirming, setCancelConfirming] = useState(false);
  const cancelAction = useAsyncAction();

  // Reactivate flow
  const reactivateAction = useAsyncAction();

  // Checkout (subscribe/resubscribe) flow
  const checkoutAction = useAsyncAction();

  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const deleteAction = useAsyncAction();

  // Derive subscription state from AuthContext teacher (updated after fetchMe)
  const subStatus = teacher?.subscription_status; // 'active'|'cancelled'|'past_due'|'expired'|null
  const isActiveSub = isSubscriber && subStatus === 'active';
  const isCancelledSub = isSubscriber && subStatus === 'cancelled'; // pending cancel, still has access
  const isLapsedSub = !isSubscriber && (subStatus === 'expired' || subStatus === 'past_due');

  const loadInvoices = useCallback(async () => {
    if (!isSubscriber && !isLapsedSub) return;
    // Errors are non-fatal here (receipts are non-critical) — the error this hook
    // captures is intentionally never rendered.
    await runInvoices(async () => {
      const res = await fetch('/api/stripe/invoices', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    });
  }, [isSubscriber, isLapsedSub, runInvoices]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleCancelSubscription = async () => {
    await cancelAction.run(async () => {
      const res = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchMe(); // refresh auth context so subscription_status updates
      setCancelConfirming(false);
    });
  };

  const handleReactivateSubscription = async () => {
    await reactivateAction.run(async () => {
      const res = await fetch('/api/stripe/reactivate-subscription', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await fetchMe();
    });
  };

  const handleStartCheckout = async () => {
    await checkoutAction.run(async () => {
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.url;
    });
  };

  const handleUpdateClassroomName = async (e) => {
    e.preventDefault();
    setClassroomNameSaved(false);
    await classroomNameAction.run(async () => {
      const res = await fetch('/api/teacher/classroom-name', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ classroom_name: classroomName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      updateTeacher({ classroom_name: data.classroom_name });
      setClassroomNameSaved(true);
      setTimeout(() => setClassroomNameSaved(false), 3000);
    });
  };

  const handleDeleteAccount = async () => {
    await deleteAction.run(async () => {
      const res = await fetch('/api/teacher/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation: deleteConfirmation }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await logout();
      navigate('/');
    });
  };

  return (
    <DashboardLayout>
      <h1 style={styles.pageHeading}>Account Settings</h1>

      {/* Profile */}
      <section style={styles.section}>
        <h2 style={styles.sectionHeading}>Profile</h2>

        <form onSubmit={handleUpdateClassroomName} style={styles.form}>
          <label style={styles.label}>
            Classroom name
            <input
              type="text"
              value={classroomName}
              onChange={(e) => setClassroomName(e.target.value)}
              style={styles.input}
              required
            />
            <span style={styles.hint}>Shown at the top of every student's bingo board</span>
          </label>
          {classroomNameAction.error && <p style={styles.error}>{classroomNameAction.error}</p>}
          {classroomNameSaved && <p style={styles.success}>Saved!</p>}
          <button type="submit" disabled={classroomNameAction.loading} style={styles.saveButton}>Save</button>
        </form>

        {!isLightweightAccount && (
          <div style={{ marginTop: '20px' }}>
            <label style={styles.label}>
              Email address
              <input type="email" value={teacher?.email || ''} style={{ ...styles.input, backgroundColor: '#f9f9f7', color: '#9ca3af' }} disabled />
            </label>
            <a href="/change-email" style={styles.textLink}>Change email address</a>
          </div>
        )}

        {!isLightweightAccount && (
          <div style={{ marginTop: '16px' }}>
            <a href="/forgot-password" style={styles.textLink}>Reset password</a>
          </div>
        )}
      </section>

      {/* Subscription */}
      <section style={styles.section}>
        <h2 style={styles.sectionHeading}>Subscription</h2>

        {/* ── Active, auto-renewing ── */}
        {isActiveSub && (
          <div style={styles.card}>
            <div style={styles.row}>
              <span style={styles.subBadge}>Active</span>
              <span style={styles.cardText}>
                Full library · $19.99/year · renews{' '}
                {teacher?.subscription_expires_at ? fmtDate(teacher.subscription_expires_at) : '—'}
              </span>
            </div>
            {cancelAction.error && <p style={styles.cardErrorText}>{cancelAction.error}</p>}
            {!cancelConfirming ? (
              <button
                onClick={() => setCancelConfirming(true)}
                style={{ ...styles.outlineButton, marginTop: '16px' }}
              >
                Cancel subscription
              </button>
            ) : (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '360px' }}>
                <p style={{ ...styles.cardText, color: '#92400e' }}>
                  You'll keep full access until{' '}
                  {teacher?.subscription_expires_at ? fmtDate(teacher.subscription_expires_at) : 'the end of your billing period'}.
                  After that, your account reverts to TpT-only access.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelAction.loading}
                    style={{ ...styles.dangerButton, opacity: cancelAction.loading ? 0.6 : 1 }}
                  >
                    {cancelAction.loading ? 'Cancelling…' : 'Confirm cancellation'}
                  </button>
                  <button
                    onClick={() => { setCancelConfirming(false); cancelAction.setError(''); }}
                    style={styles.outlineButton}
                  >
                    Keep subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Active, cancellation pending ── */}
        {isCancelledSub && (
          <div style={styles.card}>
            <div style={styles.row}>
              <span style={{ ...styles.subBadge, backgroundColor: '#fef3c7', color: '#92400e' }}>Cancelling</span>
              <span style={styles.cardText}>
                Access continues until{' '}
                {teacher?.subscription_expires_at ? fmtDate(teacher.subscription_expires_at) : '—'}
              </span>
            </div>
            <p style={{ ...styles.cardText, marginTop: '8px' }}>
              Your subscription won't renew. Custom lists and the full library remain available until the date above.
            </p>
            {reactivateAction.error && <p style={styles.cardErrorText}>{reactivateAction.error}</p>}
            <button
              onClick={handleReactivateSubscription}
              disabled={reactivateAction.loading}
              style={{ ...styles.outlineButton, marginTop: '16px', opacity: reactivateAction.loading ? 0.6 : 1 }}
            >
              {reactivateAction.loading ? 'Reactivating…' : 'Undo cancellation'}
            </button>
          </div>
        )}

        {/* ── Lapsed subscriber ── */}
        {isLapsedSub && (
          <div style={styles.card}>
            <div style={styles.row}>
              <span style={{ ...styles.subBadge, backgroundColor: '#fee2e2', color: '#dc2626' }}>Expired</span>
              <span style={styles.cardText}>
                Expired{' '}
                {teacher?.subscription_expires_at ? fmtDate(teacher.subscription_expires_at) : ''}
              </span>
            </div>
            <p style={{ ...styles.cardText, marginTop: '8px' }}>
              Your custom lists are preserved and will be restored the moment you resubscribe.
            </p>
            {teacher?.tpt_novels?.length > 0 && (
              <TptForeverNote novels={teacher.tpt_novels} showTitles={false} />
            )}
            {checkoutAction.error && <p style={styles.cardErrorText}>{checkoutAction.error}</p>}
            <button onClick={handleStartCheckout} disabled={checkoutAction.loading} style={{ ...styles.saveButton, marginTop: '16px' }}>
              Resubscribe for $19.99/yr
            </button>
          </div>
        )}

        {/* ── Never subscribed ── */}
        {!isActiveSub && !isCancelledSub && !isLapsedSub && (
          <div style={styles.card}>
            <p style={styles.cardText}>
              {isLightweightAccount
                ? 'Create a full account to subscribe and access the full novel library.'
                : "You're on the TpT-unlocked tier. Subscribe for full library access and custom uploads."}
            </p>
            {teacher?.tpt_novels?.length > 0 && (
              <TptForeverNote novels={teacher.tpt_novels} showTitles={true} />
            )}
            {checkoutAction.error && <p style={styles.cardErrorText}>{checkoutAction.error}</p>}
            <div style={{ marginTop: '16px' }}>
              {isLightweightAccount ? (
                <a href="/signup?upgrade=true" style={styles.saveButton}>Create a full account first</a>
              ) : (
                <button onClick={handleStartCheckout} disabled={checkoutAction.loading} style={styles.saveButton}>Subscribe for $19.99/yr</button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Receipts */}
      {(isActiveSub || isCancelledSub || isLapsedSub) && (
        <section style={styles.section}>
          <h2 style={styles.sectionHeading}>Receipts</h2>
          <div style={styles.card}>
            {invoicesLoading && <p style={styles.cardText}>Loading…</p>}
            {!invoicesLoading && invoices.length === 0 && (
              <p style={styles.cardText}>No receipts yet.</p>
            )}
            {!invoicesLoading && invoices.length > 0 && (
              <table style={styles.invoiceTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td style={styles.td}>{fmtDate(inv.date * 1000)}</td>
                      <td style={styles.td}>{fmtAmount(inv.amount, inv.currency)}</td>
                      <td style={styles.td}>
                        {inv.pdf_url && (
                          <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" style={styles.textLink}>
                            Download PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {/* TpT novels */}
      {teacher?.tpt_novels?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionHeading}>TpT unlocked novels</h2>
          <div style={styles.card}>
            <p style={{ ...styles.cardText, marginBottom: '12px' }}>
              These novels are permanently unlocked via your TpT printable purchase and never expire.
            </p>
            {teacher.tpt_novels.map((novel) => (
              <div key={novel.novel_id} style={styles.row}>
                <span style={styles.novelName}>{novel.title}</span>
                <span style={styles.subBadge}>Free forever</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Danger zone */}
      <section style={{ ...styles.section, borderTop: '1px solid #fee2e2', paddingTop: '24px' }}>
        <h2 style={{ ...styles.sectionHeading, color: '#dc2626' }}>Delete account</h2>
        <div style={styles.card}>
          <p style={styles.cardText}>
            This will permanently delete your account, including all custom vocabulary lists.
            This cannot be undone.
          </p>
          <div style={{ marginTop: '16px' }}>
            <label style={styles.label}>
              To confirm, type DELETE below.
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                style={styles.input}
                placeholder="DELETE"
              />
            </label>
            {deleteAction.error && <p style={styles.error}>{deleteAction.error}</p>}
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
              <button
                onClick={() => setDeleteConfirmation('')}
                disabled={deleteAction.loading}
                style={styles.modalCancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation.trim() !== 'DELETE'}
                style={{
                  ...styles.saveButton,
                  backgroundColor: '#dc2626',
                  opacity: deleteConfirmation.trim() !== 'DELETE' ? 0.4 : 1,
                }}
              >
                {deleteAction.loading ? 'Deleting…' : 'Delete my account'}
              </button>
            </div>
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}

const styles = {
  pageHeading: { fontSize: '28px', fontWeight: '700', color: 'var(--color-text)', margin: '0 0 32px' },
  section: { marginBottom: '36px' },
  sectionHeading: { fontSize: '16px', fontWeight: '600', color: 'var(--color-text)', margin: '0 0 16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px' },
  label: { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' },
  input: {
    padding: '10px 12px', border: '1px solid #d9d4c5', borderRadius: '8px',
    fontSize: '15px', color: 'var(--color-text)', backgroundColor: '#fff', outline: 'none',
  },
  hint: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
  saveButton: {
    display: 'inline-block', padding: '10px 20px', backgroundColor: 'var(--color-primary)',
    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600',
    fontSize: '14px', cursor: 'pointer', textDecoration: 'none',
  },
  outlineButton: {
    display: 'inline-block', padding: '8px 16px',
    border: '1px solid #d9d4c5', borderRadius: '8px',
    fontSize: '14px', color: '#374151', textDecoration: 'none', fontWeight: '500',
  },
  card: {
    backgroundColor: 'var(--color-background)', border: '1px solid #e5e0d5', borderRadius: '10px', padding: '20px 24px',
  },
  cardText: { fontSize: '14px', color: '#6b6b7a', margin: 0, lineHeight: '1.6' },
  cardErrorText: { fontSize: '14px', color: '#dc2626', margin: '8px 0 0', lineHeight: '1.6' },
  row: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
  novelName: { fontSize: '14px', color: '#1a1a2e', fontWeight: '500' },
  subBadge: {
    fontSize: '11px', fontWeight: '600', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '20px',
  },
  textLink: { fontSize: '13px', color: '#c0392b', textDecoration: 'none', fontWeight: '500' },
  dangerButton: {
    display: 'inline-block', padding: '8px 16px', backgroundColor: '#dc2626',
    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600',
    fontSize: '14px', cursor: 'pointer',
  },
  invoiceTable: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '6px 12px 6px 0', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e0d5' },
  td: { padding: '10px 12px 10px 0', color: '#6b6b7a', borderBottom: '1px solid #f3f0ea', verticalAlign: 'middle' },
  error: { fontSize: '13px', color: '#dc2626', margin: '4px 0 0' },
  success: { fontSize: '13px', color: '#16a34a', margin: '4px 0 0' },
  modalCancelBtn: {
    padding: '10px 20px', backgroundColor: '#fff', color: '#374151',
    border: '1px solid #d9d4c5', borderRadius: '8px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
};
